// src/components/DriverPayCalculator.js
import React, { useState, useEffect, useCallback } from 'react';
import { Calculator, Users, DollarSign, Calendar, Clock, FileText, Download, Settings, RefreshCw, TrendingUp, Save, XCircle, BarChart3, Activity, TrendingDown, Minus } from 'lucide-react';
import BBA_LOGO from '../img/as.png';

// Import Firebase
import { db, auth } from '../firebaseConfig';
import {
    doc,
    getDoc,
    setDoc,
    collection,
    query,
    getDocs,
    orderBy
} from 'firebase/firestore';

// Import React Datepicker components
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const defaultDriverData = {
    "Adisu"   : { dailyRate: 200, hourlyRate: 20, daysWorked: 9, hoursWorked: 0, expense1099: 0, comments: "-" },
    "Barnabas": { dailyRate: 220, hourlyRate: 22, daysWorked: 12, hoursWorked: 0, expense1099: 0, comments: "" },
    "Birhanu" : { dailyRate: 200, hourlyRate: 20, daysWorked: 10, hoursWorked: 0, expense1099: 0, comments: "-" },
    "Daniel"  : { dailyRate: 242, hourlyRate: 22, daysWorked: 10, hoursWorked: 0, expense1099: 0, comments: "" },
    "Eshetu"  : { dailyRate: 200, hourlyRate: 20, daysWorked: 10, hoursWorked: 0, expense1099: 0, comments: "-" },
    "Eyouel"  : { dailyRate: 220, hourlyRate: 22, daysWorked: 10, hoursWorked: 0, expense1099: 0, comments: "-" },
    "Kaleab"  : { dailyRate: 220, hourlyRate: 22, daysWorked: 10, hoursWorked: 0, expense1099: 0, comments: "-" },
    "Kirubel" : { dailyRate: 210, hourlyRate: 21, daysWorked: 12, hoursWorked: 0, expense1099: 0, comments: "-" },
    "Mulugeta": { dailyRate: 220, hourlyRate: 22, daysWorked: 10, hoursWorked: 0, expense1099: 0, comments: "-" },
    "Mussie"  : { dailyRate: 220, hourlyRate: 22, daysWorked: 10, hoursWorked: 0, expense1099: 0, comments: "-" },
    "Semira"  : { dailyRate: 140, hourlyRate: 20, daysWorked: 8, hoursWorked: 0, expense1099: 0, comments: "-" },
    "Tekle"   : { dailyRate: 276, hourlyRate: 23, daysWorked: 12, hoursWorked: 0, expense1099: 0, comments: "-" },
    "Yared"   : { dailyRate: 242, hourlyRate: 22, daysWorked: 8, hoursWorked: 0, expense1099: 0, comments: "-" },
    "Yordanos": { dailyRate: 250, hourlyRate: 25, daysWorked: 12, hoursWorked: 0, expense1099: 0, comments: "-" },
    "Zekarias": { dailyRate: 242, hourlyRate: 22, daysWorked: 9, hoursWorked: 0, expense1099: 0, comments: "-" },
};

const DriverPayCalculator = ({ viewingUserId }) => {
    const [driverData, setDriverData] = useState({});
    const [showSettings, setShowSettings] = useState(false);
    const [payResults, setPayResults] = useState([]);
    const [totalPay, setTotalPay] = useState(0);
    const [isCalculating, setIsCalculating] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [archivedWeeks, setArchivedWeeks] = useState([]);
    const [selectedArchiveId, setSelectedArchiveId] = useState('');
    const [weeklyNotes, setWeeklyNotes] = useState("");
    const [loadedArchiveDetails, setLoadedArchiveDetails] = useState(null);

    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [payDate, setPayDate] = useState(null);
    const [periodStartDate, setPeriodStartDate] = useState(null);
    const [periodEndDate, setPeriodEndDate] = useState(null);
    const [tempWeeklyNotes, setTempWeeklyNotes] = useState("");

    // Dashboard data
    const [historicalData, setHistoricalData] = useState([]);
    const [showDashboard, setShowDashboard] = useState(false);
    const [chartType, setChartType] = useState('bar'); // 'line', 'bar', 'combo', 'sparkline'
    const [dashboardStartDate, setDashboardStartDate] = useState(null);
    const [dashboardEndDate, setDashboardEndDate] = useState(null);
    const [filteredHistoricalData, setFilteredHistoricalData] = useState([]);

    // Get current user ID
    const userId = viewingUserId || auth.currentUser?.uid;

    // Function to save current driver data to Firebase (user-specific)
    const saveCurrentDataToFirebase = useCallback(async (dataToSave, currentPayResults, currentTotalPay) => {
        if (!userId) {
            console.error("No user logged in");
            return false;
        }

        try {
            const docRef = doc(db, "users", userId, "payroll", "currentData");
            await setDoc(docRef, {
                driverData: dataToSave,
                payResults: currentPayResults,
                totalPay: currentTotalPay,
                lastSaved: new Date().toISOString(),
                userEmail: auth.currentUser?.email || "unknown"
            });
            console.log("Current driver data saved to Firebase for user:", userId);
            return true;
        } catch (error) {
            console.error("Error saving current data to Firebase:", error);
            alert("Failed to save current data.");
            return false;
        }
    }, [userId]);

    // Function to fetch the list of all archived payroll weeks (user-specific)
    const fetchArchivedWeeksList = useCallback(async () => {
        if (!userId) return;

        setIsLoading(true);
        try {
            const q = query(
                collection(db, "users", userId, "payrollHistory"),
                orderBy("archiveTimestamp", "desc")
            );
            const querySnapshot = await getDocs(q);
            const weeks = querySnapshot.docs.map(doc => {
                const data = doc.data();
                let displayString = '';
                if (data.periodStartDate && data.periodEndDate) {
                    displayString += ` ${data.periodStartDate} to ${data.periodEndDate}`;
                } else if (data.archiveTimestamp) {
                    displayString += ` (Archived: ${new Date(data.archiveTimestamp).toLocaleDateString()})`;
                }
                return {
                    id: doc.id,
                    displayDate: displayString || 'N/A Date',
                    archiveTimestamp: data.archiveTimestamp,
                    totalPay: data.totalPay || 0,
                    numberOfDrivers: data.numberOfDrivers || 0,
                    periodEndDate: data.periodEndDate
                };
            });
            setArchivedWeeks(weeks);
            setHistoricalData(weeks.slice(0, 18)); // Last 18 weeks for charts
            if (weeks.length > 0 && !selectedArchiveId) {
                setSelectedArchiveId(weeks[0].id);
            }
            console.log("Archived weeks list fetched for user:", userId);
        } catch (error) {
            console.error("Error fetching archived weeks list:", error);
            alert("Failed to load list of past payrolls.");
        } finally {
            setIsLoading(false);
        }
    }, [selectedArchiveId, userId]);

    // Function to load a selected archived payroll data (user-specific)
    const loadSelectedArchive = useCallback(async () => {
        if (!selectedArchiveId || !userId) {
            alert("Please select a payroll week to load.");
            return;
        }

        setIsLoading(true);
        try {
            const docRef = doc(db, "users", userId, "payrollHistory", selectedArchiveId);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                setDriverData(data.driverData || defaultDriverData);
                setPayResults(data.payResults || []);
                setTotalPay(data.totalPay || 0);
                setWeeklyNotes("");
                setLoadedArchiveDetails({
                    payDate: data.payDate,
                    periodStartDate: data.periodStartDate,
                    periodEndDate: data.periodEndDate,
                    archiveTimestamp: data.archiveTimestamp,
                    numberOfDrivers: data.numberOfDrivers,
                    totalDailyPay: data.totalDailyPay,
                    totalHourlyPay: data.totalHourlyPay,
                    totalRegularPay: data.totalRegularPay,
                    total1099Expense: data.total1099Expense,
                    weeklyNotes: data.weeklyNotes || "No specific notes for this archived week.",
                });
                alert(`Payroll data for "${selectedArchiveId}" loaded! You can now review it.`);
                console.log("Archived data loaded:", data);
            } else {
                alert("The selected archived payroll was not found.");
                console.log(`No archived document found with ID: ${selectedArchiveId}`);
                setLoadedArchiveDetails(null);
            }
        } catch (error) {
            console.error("Error loading archived data:", error);
            alert("Failed to load archived payroll data.");
        } finally {
            setIsLoading(false);
        }
    }, [selectedArchiveId, userId]);

    const calculatePay = (hoursWorked, hourlyRate, daysWorked, dailyRate, expense1099) => {
        const hWorked = parseFloat(hoursWorked) || 0;
        const hRate = parseFloat(hourlyRate) || 0;
        const dWorked = parseFloat(daysWorked) || 0;
        const dRate = parseFloat(dailyRate) || 0;
        const exp1099 = parseFloat(expense1099) || 0;

        const hourlyPay = hWorked * hRate;
        const dailyPay = dWorked * dRate;
        const regularPay = hourlyPay + dailyPay;
        const totalPay = regularPay + exp1099;

        return { hourlyPay, dailyPay, regularPay, totalPay, expense1099: exp1099 };
    };

    const handleDriverDataChange = (driverName, field, value) => {
        setDriverData(prev => {
            const currentDriver = prev[driverName] || {};
            return {
                ...prev,
                [driverName]: {
                    ...currentDriver,
                    [field]: value
                }
            };
        });
    };

    const handleRateChange = (driverName, field, value) => {
        const numValue = value === '' ? 0 : parseFloat(value);
        setDriverData(prev => {
            const currentDriver = prev[driverName] || {};
            return {
                ...prev,
                [driverName]: {
                    ...currentDriver,
                    [field]: isNaN(numValue) ? 0 : numValue
                }
            };
        });
    };

    // Function to save rate changes
    const saveRateChanges = async () => {
        const saveSuccess = await saveCurrentDataToFirebase(driverData, payResults, totalPay);
        if (saveSuccess) {
            alert("Rate changes saved successfully!");
        }
    };

    const calculateAndSaveCurrentPayroll = useCallback(async () => {
        setIsCalculating(true);

        setTimeout(async () => {
            let totalPayAll = 0;
            const results = [];

            Object.entries(driverData).forEach(([driverName, data]) => {
                const { hoursWorked, daysWorked, expense1099, dailyRate, hourlyRate, comments } = data;

                const parsedHoursWorked = parseFloat(hoursWorked) || 0;
                const parsedDaysWorked = parseFloat(daysWorked) || 0;
                const parsedExpense1099 = parseFloat(expense1099) || 0;
                const parsedDailyRate = parseFloat(dailyRate) || 0;
                const parsedHourlyRate = parseFloat(hourlyRate) || 0;

                if (parsedHoursWorked < 0 || parsedDaysWorked < 0 || parsedExpense1099 < 0) {
                    console.warn(`Negative values found for ${driverName}, skipping calculation for this driver.`);
                    return;
                }

                const payData = calculatePay(
                    parsedHoursWorked,
                    parsedHourlyRate,
                    parsedDaysWorked,
                    parsedDailyRate,
                    parsedExpense1099
                );
                totalPayAll += payData.totalPay;

                results.push({
                    name: driverName,
                    hourlyPay: payData.hourlyPay || 0,
                    dailyPay: payData.dailyPay || 0,
                    regularPay: payData.regularPay || 0,
                    totalPay: payData.totalPay || 0,
                    expense1099: payData.expense1099 || 0,
                    daysWorked: parsedDaysWorked,
                    hoursWorked: parsedHoursWorked,
                    dailyRate: parsedDailyRate,
                    hourlyRate: parsedHourlyRate,
                    comments: comments || "-"
                });
            });

            setPayResults(results);
            setTotalPay(totalPayAll);
            setIsCalculating(false);

            const saveSuccess = await saveCurrentDataToFirebase(driverData, results, totalPayAll);
            if (saveSuccess) {
                alert("Payroll calculated and current data saved!");
            }

        }, 800);
    }, [driverData, saveCurrentDataToFirebase]);

    const resetToDefaults = async () => {
        if (window.confirm("Are you sure you want to reset all data to defaults? This will also clear the current data from Firebase.")) {
            setDriverData(defaultDriverData);
            setPayResults([]);
            setTotalPay(0);
            setWeeklyNotes("");
            setLoadedArchiveDetails(null);

            try {
                const docRef = doc(db, "users", userId, "payroll", "currentData");
                await setDoc(docRef, {
                    driverData: defaultDriverData,
                    payResults: [],
                    totalPay: 0,
                    lastSaved: new Date().toISOString(),
                    userEmail: auth.currentUser?.email || "unknown"
                });
                console.log("Firebase 'currentData' reset to defaults for user:", userId);
                alert("All current data reset successfully!");
            } catch (error) {
                console.error("Error resetting Firebase data:", error);
                alert("Failed to reset current data in Firebase.");
            }
        }
    };

    const exportToCSV = () => {
        if (payResults.length === 0) return;

        const csvContent = [
            ['Driver', 'Days Worked', 'Daily Rate', 'Daily Pay', 'Hours Worked', 'Hourly Rate', 'Hourly Pay', '1099 Expense', 'Regular Pay', 'Total Pay', 'Comments'],
            ...payResults.sort((a, b) => a.name.localeCompare(b.name)).map(result => [
                result.name,
                result.daysWorked ?? 0,
                result.dailyRate ?? 0,
                (result.dailyPay ?? 0).toFixed(2),
                result.hoursWorked ?? 0,
                result.hourlyRate ?? 0,
                (result.hourlyPay ?? 0).toFixed(2),
                (result.expense1099 ?? 0).toFixed(2),
                (result.regularPay ?? 0).toFixed(2),
                (result.totalPay ?? 0).toFixed(2),
                result.comments || "-"
            ])
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'driver_pay_summary.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportData = () => {
        const dataToExport = {
            driverData,
            payResults,
            totalPay,
            weeklyNotes,
            exportDate: new Date().toISOString(),
            userId: userId
        };

        const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `driver_pay_backup_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const importData = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const importedData = JSON.parse(e.target.result);
                if (importedData.driverData) {
                    setDriverData(importedData.driverData);
                    setPayResults(importedData.payResults || []);
                    setTotalPay(importedData.totalPay || 0);
                    setWeeklyNotes(importedData.weeklyNotes || "");
                    setLoadedArchiveDetails(null);

                    const saveSuccess = await saveCurrentDataToFirebase(
                        importedData.driverData,
                        importedData.payResults || [],
                        importedData.totalPay || 0
                    );
                    if (saveSuccess) {
                        alert("Data imported successfully and saved to Firebase as current data!");
                    }
                } else {
                    alert('Invalid file format: Missing driverData.');
                }
            } catch (error) {
                console.error("Error importing data:", error);
                alert('Invalid file format. Please select a valid backup file.');
            }
        };
        reader.readAsText(file);
    };

    const handleArchiveClick = () => {
        if (payResults.length === 0) {
            alert("Please calculate payroll first before archiving.");
            return;
        }
        setTempWeeklyNotes(weeklyNotes);

        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);

        setPayDate(today);
        setPeriodStartDate(sevenDaysAgo);
        setPeriodEndDate(today);

        setShowArchiveModal(true);
    };

    const finalizeAndArchivePayroll = useCallback(async () => {
        if (!payDate || !periodStartDate || !periodEndDate || !userId) {
            alert("Please select a Pay Date, Period Start Date, and Period End Date.");
            return;
        }

        if (periodStartDate.getTime() > periodEndDate.getTime()) {
            alert("Period Start Date cannot be after Period End Date.");
            return;
        }

        setIsCalculating(true);
        setShowArchiveModal(false);

        try {
            let currentTotalPay = 0;
            const currentPayResults = [];
            let totalDailyPay = 0;
            let totalHourlyPay = 0;
            let totalRegularPayCalculated = 0;
            let total1099Expense = 0;

            Object.entries(driverData).forEach(([driverName, data]) => {
                const { hoursWorked, daysWorked, expense1099, dailyRate, hourlyRate, comments } = data;
                const payData = calculatePay(
                    parseFloat(hoursWorked) || 0,
                    parseFloat(hourlyRate) || 0,
                    parseFloat(daysWorked) || 0,
                    parseFloat(dailyRate) || 0,
                    parseFloat(expense1099) || 0
                );
                currentTotalPay += payData.totalPay;
                totalDailyPay += payData.dailyPay;
                totalHourlyPay += payData.hourlyPay;
                totalRegularPayCalculated += payData.regularPay;
                total1099Expense += payData.expense1099;

                currentPayResults.push({
                    name: driverName,
                    hourlyPay: payData.hourlyPay || 0,
                    dailyPay: payData.dailyPay || 0,
                    regularPay: payData.regularPay || 0,
                    totalPay: payData.totalPay || 0,
                    expense1099: payData.expense1099 || 0,
                    daysWorked: parseFloat(daysWorked) || 0,
                    hoursWorked: parseFloat(hoursWorked) || 0,
                    dailyRate: parseFloat(dailyRate) || 0,
                    hourlyRate: parseFloat(hourlyRate) || 0,
                    comments: comments || "-"
                });
            });

            setPayResults(currentPayResults);
            setTotalPay(currentTotalPay);

            const archiveTimestamp = new Date().toISOString();
            const payDateISO = payDate.toISOString().split('T')[0];
            const periodStartDateISO = periodStartDate.toISOString().split('T')[0];
            const periodEndDateISO = periodEndDate.toISOString().split('T')[0];

            const payrollWeekId = `payroll_paydate_${payDateISO}`;
            const numberOfDrivers = Object.keys(driverData).length;

            const historyDocRef = doc(db, "users", userId, "payrollHistory", payrollWeekId);

            await setDoc(historyDocRef, {
                driverData: driverData,
                payResults: currentPayResults,
                totalPay: currentTotalPay,
                payDate: payDateISO,
                periodStartDate: periodStartDateISO,
                periodEndDate: periodEndDateISO,
                archiveTimestamp: archiveTimestamp,
                numberOfDrivers: numberOfDrivers,
                totalDailyPay: totalDailyPay,
                totalHourlyPay: totalHourlyPay,
                totalRegularPay: totalRegularPayCalculated,
                total1099Expense: total1099Expense,
                weeklyNotes: tempWeeklyNotes,
            });

            console.log(`Current payroll archived as: ${payrollWeekId} for user: ${userId}`);
            alert(`Payroll successfully archived for Pay Date: ${payDateISO}!`);

            await saveCurrentDataToFirebase(defaultDriverData, [], 0);
            setDriverData(defaultDriverData);
            setPayResults([]);
            setTotalPay(0);
            setWeeklyNotes("");
            setLoadedArchiveDetails(null);

            setPayDate(null);
            setPeriodStartDate(null);
            setPeriodEndDate(null);
            setTempWeeklyNotes("");

            await fetchArchivedWeeksList();

        } catch (error) {
            console.error("Error archiving payroll:", error);
            alert("Failed to archive payroll data.");
        } finally {
            setIsCalculating(false);
        }
    }, [driverData, tempWeeklyNotes, payDate, periodStartDate, periodEndDate, userId, saveCurrentDataToFirebase, fetchArchivedWeeksList]);

    // Calculate dashboard stats
    const getDashboardStats = () => {
        const activeDrivers = payResults.length > 0 ? payResults.filter(r => r.totalPay > 0).length : Object.keys(driverData).length;
        const avgPay = payResults.length > 0 ? totalPay / payResults.filter(r => r.totalPay > 0).length : 0;
        const topEarner = payResults.length > 0 ? payResults.reduce((max, r) => r.totalPay > max.totalPay ? r : max, payResults[0]) : null;
        
        return {
            activeDrivers,
            avgPay: isNaN(avgPay) ? 0 : avgPay,
            topEarner: topEarner?.name || 'N/A',
            topEarnerPay: topEarner?.totalPay || 0
        };
    };

    // Calculate trend metrics
    const getTrendMetrics = () => {
        const dataToUse = filteredHistoricalData.length > 0 ? filteredHistoricalData : historicalData;
        if (dataToUse.length < 2) return null;
        
        const reversedData = [...dataToUse].reverse();
        const average = reversedData.reduce((sum, w) => sum + w.totalPay, 0) / reversedData.length;
        const highest = reversedData.reduce((max, w) => w.totalPay > max.totalPay ? w : max, reversedData[0]);
        const lowest = reversedData.reduce((min, w) => w.totalPay < min.totalPay ? w : min, reversedData[0]);
        
        // Calculate trend (up, down, stable)
        const lastTwo = reversedData.slice(-2);
        let trend = 'stable';
        let trendPercent = 0;
        if (lastTwo.length === 2) {
            const diff = lastTwo[1].totalPay - lastTwo[0].totalPay;
            trendPercent = lastTwo[0].totalPay > 0 ? (diff / lastTwo[0].totalPay) * 100 : 0;
            if (Math.abs(trendPercent) > 2) {
                trend = trendPercent > 0 ? 'up' : 'down';
            }
        }
        
        return { average, highest, lowest, trend, trendPercent };
    };

    // Render different chart types
    const renderChart = () => {
        const dataToUse = filteredHistoricalData.length > 0 ? filteredHistoricalData : historicalData;
        const reversedData = [...dataToUse].reverse();
        const maxPay = Math.max(...reversedData.map(w => w.totalPay));
        const metrics = getTrendMetrics();
        
        if (chartType === 'line') {
            return (
                <div>
                    <div className="relative" style={{height: '16rem'}}>
                        {/* Average line */}
                        {metrics && (
                            <div 
                                className="absolute w-full border-t-2 border-dashed border-yellow-500/50"
                                style={{ bottom: `${(metrics.average / maxPay) * 100}%` }}
                            >
                                <span className="absolute -top-3 right-0 text-xs text-yellow-400">Avg: ${metrics.average.toFixed(2)}</span>
                            </div>
                        )}
                        
                        {/* SVG Line Chart */}
                        <svg className="w-full h-full" viewBox="0 0 600 250" preserveAspectRatio="none">
                            {/* Grid lines */}
                            {[0, 25, 50, 75, 100].map(percent => (
                                <line 
                                    key={percent}
                                    x1="0" 
                                    y1={250 - (percent * 2.5)} 
                                    x2="600" 
                                    y2={250 - (percent * 2.5)}
                                    stroke="rgba(255,255,255,0.1)" 
                                    strokeWidth="1"
                                />
                            ))}
                            
                            {/* Line path */}
                            <polyline
                                points={reversedData.map((week, index) => {
                                    const x = (index / (reversedData.length - 1)) * 600;
                                    const y = 250 - ((week.totalPay / maxPay) * 230);
                                    return `${x},${y}`;
                                }).join(' ')}
                                fill="none"
                                stroke="url(#lineGradient)"
                                strokeWidth="3"
                                strokeLinecap="round"
                            />
                            
                            {/* Gradient definition */}
                            <defs>
                                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#3b82f6" />
                                    <stop offset="100%" stopColor="#a855f7" />
                                </linearGradient>
                            </defs>
                            
                            {/* Data points */}
                            {reversedData.map((week, index) => {
                                const x = (index / (reversedData.length - 1)) * 600;
                                const y = 250 - ((week.totalPay / maxPay) * 230);
                                const isHighest = metrics && week.id === metrics.highest.id;
                                const isLowest = metrics && week.id === metrics.lowest.id;
                                
                                return (
                                    <g key={week.id}>
                                        <circle
                                            cx={x}
                                            cy={y}
                                            r={isHighest || isLowest ? "8" : "5"}
                                            fill={isHighest ? "#fbbf24" : isLowest ? "#ef4444" : "#fff"}
                                            stroke={isHighest ? "#fbbf24" : isLowest ? "#ef4444" : "#3b82f6"}
                                            strokeWidth="2"
                                        />
                                    </g>
                                );
                            })}
                        </svg>
                    </div>
                    
                    {/* Labels with better spacing */}
                    <div className="mt-4 px-2 pb-2">
                        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${reversedData.length}, minmax(0, 1fr))` }}>
                            {reversedData.map((week, index) => (
                                <div key={week.id} className="flex flex-col items-center text-center min-w-0">
                                    <div className="text-xs text-gray-400 truncate w-full px-1" title={week.periodEndDate}>
                                        {week.periodEndDate ? new Date(week.periodEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : `W${index + 1}`}
                                    </div>
                                    <div className="text-sm text-blue-400 font-semibold mt-1">
                                        ${(week.totalPay / 1000).toFixed(1)}k
                                    </div>
                                    {index > 0 && (
                                        <div className="flex items-center justify-center mt-1">
                                            {(() => {
                                                const prevPay = reversedData[index - 1].totalPay;
                                                const change = ((week.totalPay - prevPay) / prevPay) * 100;
                                                if (change > 0) {
                                                    return (
                                                        <span className="text-green-400 flex items-center text-xs">
                                                            <TrendingUp className="w-3 h-3" />
                                                            <span className="ml-0.5">{change.toFixed(0)}%</span>
                                                        </span>
                                                    );
                                                } else if (change < 0) {
                                                    return (
                                                        <span className="text-red-400 flex items-center text-xs">
                                                            <TrendingDown className="w-3 h-3" />
                                                            <span className="ml-0.5">{Math.abs(change).toFixed(0)}%</span>
                                                        </span>
                                                    );
                                                } else {
                                                    return (
                                                        <span className="text-gray-400 flex items-center text-xs">
                                                            <Minus className="w-3 h-3" />
                                                            <span className="ml-0.5">0%</span>
                                                        </span>
                                                    );
                                                }
                                            })()}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }
        
        if (chartType === 'bar') {
            return (
                <div className="space-y-3">
                    {reversedData.map((week, index) => {
                        const percentage = (week.totalPay / maxPay) * 100;
                        const isHighest = metrics && week.id === metrics.highest.id;
                        const isLowest = metrics && week.id === metrics.lowest.id;
                        const prevPay = index > 0 ? reversedData[index - 1].totalPay : null;
                        const change = prevPay ? ((week.totalPay - prevPay) / prevPay) * 100 : null;
                        
                        return (
                            <div key={week.id}>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-400 flex items-center gap-2">
                                        {week.periodEndDate || `Week ${index + 1}`}
                                        {isHighest && <span className="text-yellow-400">🏆 Highest</span>}
                                        {isLowest && <span className="text-red-400">📉 Lowest</span>}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-blue-400 font-semibold">${week.totalPay.toFixed(2)}</span>
                                        {change !== null && (
                                            <span className={`text-xs flex items-center ${change > 0 ? 'text-green-400' : change < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                                                {change > 0 ? <TrendingUp className="w-3 h-3" /> : change < 0 ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                                                {Math.abs(change).toFixed(1)}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="w-full bg-white/10 rounded-full h-4 overflow-hidden relative">
                                    <div 
                                        className={`h-full rounded-full transition-all duration-500 ease-out ${
                                            isHighest ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                                            isLowest ? 'bg-gradient-to-r from-red-500 to-pink-500' :
                                            'bg-gradient-to-r from-blue-500 to-purple-500'
                                        }`}
                                        style={{ width: `${percentage}%` }}
                                    ></div>
                                    {/* Average marker */}
                                    {metrics && (
                                        <div 
                                            className="absolute top-0 h-full w-0.5 bg-yellow-400"
                                            style={{ left: `${(metrics.average / maxPay) * 100}%` }}
                                        ></div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }
        
        if (chartType === 'combo') {
            return (
                <div className="space-y-4">
                    {/* Line chart on top */}
                    <div className="relative h-32">
                        <svg className="w-full h-full" viewBox="0 0 600 120" preserveAspectRatio="none">
                            <polyline
                                points={reversedData.map((week, index) => {
                                    const x = (index / (reversedData.length - 1)) * 600;
                                    const y = 120 - ((week.totalPay / maxPay) * 110);
                                    return `${x},${y}`;
                                }).join(' ')}
                                fill="none"
                                stroke="url(#comboGradient)"
                                strokeWidth="2"
                            />
                            <defs>
                                <linearGradient id="comboGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#3b82f6" />
                                    <stop offset="100%" stopColor="#a855f7" />
                                </linearGradient>
                            </defs>
                            {reversedData.map((week, index) => {
                                const x = (index / (reversedData.length - 1)) * 600;
                                const y = 120 - ((week.totalPay / maxPay) * 110);
                                return <circle key={week.id} cx={x} cy={y} r="4" fill="#fff" />;
                            })}
                        </svg>
                    </div>
                    
                    {/* Bars below */}
                    <div className="grid grid-cols-6 gap-2">
                        {reversedData.map((week, index) => {
                            const percentage = (week.totalPay / maxPay) * 100;
                            return (
                                <div key={week.id} className="text-center">
                                    <div className="h-16 flex items-end">
                                        <div 
                                            className="w-full bg-gradient-to-t from-blue-500 to-purple-500 rounded-t transition-all duration-500"
                                            style={{ height: `${percentage}%` }}
                                        ></div>
                                    </div>
                                    <div className="text-xs text-gray-400 mt-1">{week.periodEndDate?.split('-')[1] || `W${index + 1}`}</div>
                                    <div className="text-xs text-blue-400">${(week.totalPay / 1000).toFixed(1)}k</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }
        
        if (chartType === 'sparkline') {
            return (
                <div className="space-y-4">
                    {/* Large sparkline */}
                    <div className="relative h-24 flex items-center">
                        <svg className="w-full h-full" viewBox="0 0 600 100" preserveAspectRatio="none">
                            {/* Fill area */}
                            <polygon
                                points={reversedData.map((week, index) => {
                                    const x = (index / (reversedData.length - 1)) * 600;
                                    const y = 100 - ((week.totalPay / maxPay) * 90);
                                    return `${x},${y}`;
                                }).join(' ') + ' 600,100 0,100'}
                                fill="url(#sparkGradient)"
                                opacity="0.3"
                            />
                            
                            {/* Line */}
                            <polyline
                                points={reversedData.map((week, index) => {
                                    const x = (index / (reversedData.length - 1)) * 600;
                                    const y = 100 - ((week.totalPay / maxPay) * 90);
                                    return `${x},${y}`;
                                }).join(' ')}
                                fill="none"
                                stroke="#3b82f6"
                                strokeWidth="3"
                                strokeLinecap="round"
                            />
                            
                            <defs>
                                <linearGradient id="sparkGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.5" />
                                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            
                            {/* Last point highlight */}
                            {reversedData.length > 0 && (
                                <circle
                                    cx={600}
                                    cy={100 - ((reversedData[reversedData.length - 1].totalPay / maxPay) * 90)}
                                    r="6"
                                    fill="#3b82f6"
                                />
                            )}
                        </svg>
                    </div>
                    
                    {/* Stats summary */}
                    {metrics && (
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-3 bg-white/5 rounded-lg">
                                <div className="text-xs text-gray-400">Trend</div>
                                <div className="flex items-center justify-center gap-1 text-lg font-bold">
                                    {metrics.trend === 'up' ? (
                                        <>
                                            <TrendingUp className="w-5 h-5 text-green-400" />
                                            <span className="text-green-400">+{metrics.trendPercent.toFixed(1)}%</span>
                                        </>
                                    ) : metrics.trend === 'down' ? (
                                        <>
                                            <TrendingDown className="w-5 h-5 text-red-400" />
                                            <span className="text-red-400">{metrics.trendPercent.toFixed(1)}%</span>
                                        </>
                                    ) : (
                                        <>
                                            <Minus className="w-5 h-5 text-gray-400" />
                                            <span className="text-gray-400">Stable</span>
                                        </>
                                    )}
                                </div>
                            </div>
                            
                            <div className="text-center p-3 bg-white/5 rounded-lg">
                                <div className="text-xs text-gray-400">Average</div>
                                <div className="text-lg font-bold text-yellow-400">${metrics.average.toFixed(2)}</div>
                            </div>
                            
                            <div className="text-center p-3 bg-white/5 rounded-lg">
                                <div className="text-xs text-gray-400">Range</div>
                                <div className="text-sm font-bold text-blue-400">
                                    ${metrics.lowest.totalPay.toFixed(0)} - ${metrics.highest.totalPay.toFixed(0)}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Week details */}
                    <div className="grid grid-cols-6 gap-2 text-xs">
                        {reversedData.map((week, index) => (
                            <div key={week.id} className="text-center">
                                <div className="text-gray-400">{week.periodEndDate?.split('-').slice(1).join('/') || `W${index + 1}`}</div>
                                <div className="text-blue-400 font-semibold">${(week.totalPay / 1000).toFixed(1)}k</div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
    };

    // Filter historical data based on date range
    useEffect(() => {
        if (historicalData.length === 0) {
            setFilteredHistoricalData([]);
            return;
        }

        let filtered = [...historicalData];

        if (dashboardStartDate || dashboardEndDate) {
            filtered = filtered.filter(week => {
                if (!week.periodEndDate) return true;
                
                const weekDate = new Date(week.periodEndDate);
                const start = dashboardStartDate ? new Date(dashboardStartDate) : null;
                const end = dashboardEndDate ? new Date(dashboardEndDate) : null;

                if (start && end) {
                    return weekDate >= start && weekDate <= end;
                } else if (start) {
                    return weekDate >= start;
                } else if (end) {
                    return weekDate <= end;
                }
                return true;
            });
        }

        setFilteredHistoricalData(filtered.length > 0 ? filtered : historicalData);
    }, [historicalData, dashboardStartDate, dashboardEndDate]);

    // Initial data load for 'currentData' on component mount (user-specific)
    useEffect(() => {
        const loadInitialData = async () => {
            if (!userId) {
                console.error("No user ID available");
                return;
            }

            setIsLoading(true);
            try {
                const docRef = doc(db, "users", userId, "payroll", "currentData");
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setDriverData(data.driverData || defaultDriverData);
                    setPayResults(data.payResults || []);
                    setTotalPay(data.totalPay || 0);
                    setWeeklyNotes("");
                    console.log("Current driver data loaded from Firebase for user:", userId);
                } else {
                    console.log("No 'currentData' found for user, initializing with defaults and saving.");
                    await setDoc(docRef, {
                        driverData: defaultDriverData,
                        payResults: [],
                        totalPay: 0,
                        lastSaved: new Date().toISOString(),
                        userEmail: auth.currentUser?.email || "unknown" 
                    });
                    setDriverData(defaultDriverData);
                    setPayResults([]);
                    setTotalPay(0);
                }
            } catch (error) {
                console.error("Error loading initial current data:", error);
                alert("Failed to load initial data. Using default data. Please check console.");
                setDriverData(defaultDriverData);
                setPayResults([]);
                setTotalPay(0);
            } finally {
                setIsLoading(false);
            }
        };

        loadInitialData();
        fetchArchivedWeeksList();
    }, [userId, fetchArchivedWeeksList]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-900 text-white flex items-center justify-center">
                <RefreshCw className="w-10 h-10 animate-spin mr-3" />
                <p className="text-xl">Loading data from server...</p>
            </div>
        );
    }

    const getSortedDriverNames = () => {
        return Object.keys(driverData).sort((a, b) => a.localeCompare(b));
    };

    const stats = getDashboardStats();

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-900">
            {/* Animated background */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -inset-10 opacity-20">
                    <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
                    <div className="absolute top-3/4 right-1/4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
                    <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-500"></div>
                </div>
            </div>

            <div className="relative z-10 container mx-auto px-4 py-8">
                {/* Header */}
                <div className="text-center mb-6">
                    <div className="flex justify-center ">
                        <img src={BBA_LOGO} alt="BBA Logo" className="w-60 h-30 rounded-full " />
                    </div>
                    <div className="text-3xl font-bold mb-4 text-white "> BBA Payroll Calculator</div>
                    {userId && (
                        <div className="text-sm text-gray-400">
                            User ID: {userId.substring(0, 8)}...
                        </div>
                    )}
                </div>

                {/* Dashboard Toggle */}
                <div className="flex justify-center mb-6">
                    <button
                        onClick={() => setShowDashboard(!showDashboard)}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-500 text-white rounded-lg hover:from-blue-500 hover:to-purple-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                    >
                        <BarChart3 className="w-5 h-5 mr-2" />
                        {showDashboard ? 'Hide Dashboard' : 'Show Dashboard'}
                    </button>
                </div>

                {/* Dashboard Stats */}
                {showDashboard && (
                    <div className="mb-8">
                        {/* Date Range Filter */}
                        <div className="mb-6 bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 relative z-50">
                            <div className="flex flex-wrap items-center gap-4">
                                <span className="text-white font-medium">Filter by Date Range:</span>
                                <div className="flex items-center gap-2">
                                    <label className="text-gray-300 text-sm">From:</label>
                                    <div className="relative z-50">
                                        <DatePicker
                                            selected={dashboardStartDate}
                                            onChange={(date) => setDashboardStartDate(date)}
                                            selectsStart
                                            startDate={dashboardStartDate}
                                            endDate={dashboardEndDate}
                                            className="px-3 py-2 rounded-lg bg-slate-800 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer text-sm w-36"
                                            dateFormat="MM/dd/yyyy"
                                            placeholderText="Start date"
                                            isClearable
                                            popperClassName="z-50"
                                            popperPlacement="bottom-start"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-gray-300 text-sm">To:</label>
                                    <div className="relative z-50">
                                        <DatePicker
                                            selected={dashboardEndDate}
                                            onChange={(date) => setDashboardEndDate(date)}
                                            selectsEnd
                                            startDate={dashboardStartDate}
                                            endDate={dashboardEndDate}
                                            minDate={dashboardStartDate}
                                            className="px-3 py-2 rounded-lg bg-slate-800 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer text-sm w-36"
                                            dateFormat="MM/dd/yyyy"
                                            placeholderText="End date"
                                            isClearable
                                            popperClassName="z-50"
                                            popperPlacement="bottom-start"
                                        />
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setDashboardStartDate(null);
                                        setDashboardEndDate(null);
                                    }}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition-all text-sm font-medium"
                                >
                                    Clear Filters
                                </button>
                                <div className="ml-auto text-sm text-gray-400">
                                    Showing {filteredHistoricalData.length || historicalData.length} periods
                                </div>
                            </div>
                        </div>

                        {/* Quick Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                            {/* Total Payroll Card */}
                            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 hover:-translate-y-1">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 bg-blue-500/20 rounded-xl">
                                        <DollarSign className="w-6 h-6 text-blue-400" />
                                    </div>
                                    <TrendingUp className="w-5 h-5 text-green-400" />
                                </div>
                                <h3 className="text-gray-400 text-sm font-medium mb-1">Total Payroll</h3>
                                <p className="text-3xl font-bold text-white">${totalPay.toFixed(2)}</p>
                                <p className="text-xs text-gray-500 mt-2">Current period</p>
                            </div>

                            {/* Active Drivers Card */}
                            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 hover:-translate-y-1">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 bg-purple-500/20 rounded-xl">
                                        <Users className="w-6 h-6 text-purple-400" />
                                    </div>
                                    <Activity className="w-5 h-5 text-purple-400" />
                                </div>
                                <h3 className="text-gray-400 text-sm font-medium mb-1">Active Drivers</h3>
                                <p className="text-3xl font-bold text-white">{stats.activeDrivers}</p>
                                <p className="text-xs text-gray-500 mt-2">Total: {Object.keys(driverData).length}</p>
                            </div>

                            {/* Average Pay Card */}
                            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl hover:shadow-pink-500/50 transition-all duration-300 hover:-translate-y-1">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 bg-pink-500/20 rounded-xl">
                                        <TrendingUp className="w-6 h-6 text-pink-400" />
                                    </div>
                                    <BarChart3 className="w-5 h-5 text-pink-400" />
                                </div>
                                <h3 className="text-gray-400 text-sm font-medium mb-1">Average Pay</h3>
                                <p className="text-3xl font-bold text-white">${stats.avgPay.toFixed(2)}</p>
                                <p className="text-xs text-gray-500 mt-2">Per driver</p>
                            </div>

                            {/* Top Earner Card */}
                            {/* <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl hover:shadow-yellow-500/50 transition-all duration-300 hover:-translate-y-1">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 bg-yellow-500/20 rounded-xl">
                                        <Award className="w-6 h-6 text-yellow-400" />
                                    </div>
                                    <span className="text-2xl">🏆</span>
                                </div>
                                <h3 className="text-gray-400 text-sm font-medium mb-1">Top Earner</h3>
                                <p className="text-xl font-bold text-white truncate">{stats.topEarner}</p>
                                <p className="text-sm text-blue-400 mt-1">${stats.topEarnerPay.toFixed(2)}</p>
                            </div> */}
                        </div>

                        {/* Charts Section */}
                        {(filteredHistoricalData.length > 0 || historicalData.length > 0) && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                                {/* Payroll Trend Chart */}
                                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                                    <div className="flex items-center justify-between mb-6">
                                        <h3 className="text-xl font-bold text-white flex items-center">
                                            <TrendingUp className="w-5 h-5 mr-2 text-blue-400" />
                                            Payroll Trend ({filteredHistoricalData.length || historicalData.length} Periods)
                                        </h3>
                                        
                                        {/* Chart Type Selector */}
                                        <div className="flex gap-1 bg-white/5 rounded-lg p-1">
                                            {/* <button
                                                onClick={() => setChartType('line')}
                                                className={`px-3 py-1 rounded text-xs transition-all ${
                                                    chartType === 'line' 
                                                        ? 'bg-blue-500 text-white' 
                                                        : 'text-gray-400 hover:text-white'
                                                }`}
                                                title="Line Chart"
                                            >
                                                Line
                                            </button> */}
                                            <button
                                                onClick={() => setChartType('bar')}
                                                className={`px-3 py-1 rounded text-xs transition-all ${
                                                    chartType === 'bar' 
                                                        ? 'bg-blue-500 text-white' 
                                                        : 'text-gray-400 hover:text-white'
                                                }`}
                                                title="Bar Chart"
                                            >
                                                Bar
                                            </button>
                                            <button
                                                onClick={() => setChartType('combo')}
                                                className={`px-3 py-1 rounded text-xs transition-all ${
                                                    chartType === 'combo' 
                                                        ? 'bg-blue-500 text-white' 
                                                        : 'text-gray-400 hover:text-white'
                                                }`}
                                                title="Combo Chart"
                                            >
                                                Combo
                                            </button>
                                            {/* <button
                                                onClick={() => setChartType('sparkline')}
                                                className={`px-3 py-1 rounded text-xs transition-all ${
                                                    chartType === 'sparkline' 
                                                        ? 'bg-blue-500 text-white' 
                                                        : 'text-gray-400 hover:text-white'
                                                }`}
                                                title="Sparkline"
                                            >
                                                Spark
                                            </button> */}
                                        </div>
                                    </div>
                                    
                                    {renderChart()}
                                </div>

                                {/* Driver Comparison Chart */}
                                {/* <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                                    <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                                        <BarChart3 className="w-5 h-5 mr-2 text-purple-400" />
                                        Top 5 Drivers (Current Period)
                                    </h3>
                                    <div className="space-y-4">
                                        {payResults
                                            .sort((a, b) => b.totalPay - a.totalPay)
                                            .slice(0, 5)
                                            .map((driver, index) => {
                                                const maxPay = payResults.length > 0 ? Math.max(...payResults.map(d => d.totalPay)) : 1;
                                                const percentage = (driver.totalPay / maxPay) * 100;
                                                return (
                                                    <div key={driver.name}>
                                                        <div className="flex justify-between text-sm mb-1">
                                                            <span className="text-gray-400 flex items-center">
                                                                {index === 0 && <Award className="w-4 h-4 mr-1 text-yellow-400" />}
                                                                {driver.name}
                                                            </span>
                                                            <span className="text-purple-400 font-semibold">${driver.totalPay.toFixed(2)}</span>
                                                        </div>
                                                        <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                                                            <div 
                                                                className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full transition-all duration-500 ease-out"
                                                                style={{ width: `${percentage}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        {payResults.length === 0 && (
                                            <div className="text-center text-gray-500 py-8">
                                                Calculate payroll to see driver comparison
                                            </div>
                                        )}
                                    </div>
                                </div> */}
                            </div>
                        )}
                    </div>
                )}

                {/* Controls */}
                <div className="flex flex-wrap justify-center gap-4 mb-8">
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-lg hover:from-slate-600 hover:to-slate-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                    >
                        <Settings className="w-5 h-5 mr-2" />
                        Rate Settings
                    </button>

                    <button
                        onClick={calculateAndSaveCurrentPayroll}
                        disabled={isCalculating}
                        className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-500 text-white rounded-lg hover:from-blue-500 hover:to-purple-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isCalculating ? (
                            <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        ) : (
                            <Calculator className="w-5 h-5 mr-2" />
                        )}
                        {isCalculating ? 'Calculating...' : 'Calculate & Save Current Pay'}
                    </button>

                    <button
                        onClick={handleArchiveClick}
                        disabled={isCalculating || payResults.length === 0}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-700 text-white rounded-lg hover:from-green-500 hover:to-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Save className="w-5 h-5 mr-2" />
                        Finalize & Archive Payroll
                    </button>

                    <button
                        onClick={exportToCSV}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg hover:from-green-500 hover:to-teal-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                    >
                        <Download className="w-5 h-5 mr-2" />
                        Export CSV
                    </button>

                    <button
                        onClick={exportData}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg hover:from-cyan-500 hover:to-blue-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                    >
                        <Download className="w-5 h-5 mr-2" />
                        Backup JSON
                    </button>

                    <label className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-yellow-600 to-orange-600 text-white rounded-lg hover:from-yellow-500 hover:to-orange-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 cursor-pointer">
                        <FileText className="w-5 h-5 mr-2" />
                        Import JSON
                        <input
                            type="file"
                            accept=".json"
                            onChange={importData}
                            className="hidden"
                        />
                    </label>

                    <button
                        onClick={resetToDefaults}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-lg hover:from-red-500 hover:to-pink-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                    >
                        <RefreshCw className="w-5 h-5 mr-2" />
                        Reset All
                    </button>
                </div>

                {/* Settings Panel */}
                {showSettings && (
                    <div className="mb-8 bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                        <div className="flex items-center mb-6">
                            <Settings className="w-6 h-6 text-blue-400 mr-3" />
                            <h2 className="text-2xl font-bold text-white">Driver Rate Settings</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {getSortedDriverNames().map((driverName) => {
                                const data = driverData[driverName];
                                return (
                                    <div key={driverName} className="bg-white/5 rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-all duration-300">
                                        <h3 className="text-lg font-semibold text-white mb-3">{driverName}</h3>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-sm text-gray-300 mb-1">Daily Rate ($)</label>
                                                <input
                                                    type="number"
                                                    value={data.dailyRate}
                                                    onChange={(e) => handleRateChange(driverName, 'dailyRate', e.target.value)}
                                                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm text-gray-300 mb-1">Hourly Rate ($)</label>
                                                <input
                                                    type="number"
                                                    value={data.hourlyRate}
                                                    onChange={(e) => handleRateChange(driverName, 'hourlyRate', e.target.value)}
                                                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        {/* Save Rate Changes Button */}
                        <div className="mt-6 text-center">
                            <button
                                onClick={saveRateChanges}
                                className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-500 hover:to-purple-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                            >
                                <Save className="w-5 h-5 mr-2" />
                                Save Rate Changes
                            </button>
                        </div>
                    </div>
                )}

                {/* Input for Weekly Notes */}
                <div className="mb-8 bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                    <div className="flex items-center mb-6">
                        <FileText className="w-6 h-6 text-yellow-400 mr-3" />
                        <h2 className="text-2xl font-bold text-white">Overall Weekly Notes (Current Period)</h2>
                    </div>
                    <textarea
                        value={weeklyNotes}
                        onChange={(e) => setWeeklyNotes(e.target.value)}
                        rows="3"
                        placeholder="Add any general notes for this payroll week (these can be finalized in the archive popup)..."
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    ></textarea>
                </div>

                {/* Driver Data Input */}
                <div className="mb-8 bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                    <div className="flex items-center mb-6">
                        <Users className="w-6 h-6 text-purple-400 mr-3" />
                        <h2 className="text-2xl font-bold text-white">Current Week's Driver Work Details</h2>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-white/20">
                                    <th className="text-left py-3 px-4 font-semibold text-gray-300">Driver</th>
                                    <th className="text-left py-3 px-4 font-semibold text-gray-300">
                                        <div className="flex items-center">
                                            <Calendar className="w-4 h-4 mr-1" />
                                            Days
                                        </div>
                                    </th>
                                    <th className="text-left py-3 px-4 font-semibold text-gray-300">
                                        <div className="flex items-center">
                                            <Clock className="w-4 h-4 mr-1" />
                                            Hours
                                        </div>
                                    </th>
                                    <th className="text-left py-3 px-4 font-semibold text-gray-300">
                                        <div className="flex items-center">
                                            <DollarSign className="w-4 h-4 mr-1" />
                                            1099 Expense
                                        </div>
                                    </th>
                                    <th className="text-left py-3 px-4 font-semibold text-gray-300">
                                        <div className="flex items-center">
                                            <FileText className="w-4 h-4 mr-1" />
                                            Notes
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {getSortedDriverNames().map((driverName) => {
                                    const data = driverData[driverName];
                                    return (
                                        <tr key={driverName} className="border-b border-white/10 hover:bg-white/5 transition-colors duration-200">
                                            <td className="py-3 px-4 font-medium text-white">{driverName}</td>
                                            <td className="py-3 px-4">
                                                <input
                                                    type="number"
                                                    value={data.daysWorked}
                                                    onChange={(e) => handleDriverDataChange(driverName, 'daysWorked', e.target.value)}
                                                    className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    min="0"
                                                    step="1"
                                                />
                                            </td>
                                            <td className="py-3 px-4">
                                                <input
                                                    type="number"
                                                    value={data.hoursWorked}
                                                    onChange={(e) => handleDriverDataChange(driverName, 'hoursWorked', e.target.value)}
                                                    className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    min="0"
                                                    step="1"
                                                />
                                            </td>
                                            <td className="py-3 px-4">
                                                <input
                                                    type="number"
                                                    value={data.expense1099}
                                                    onChange={(e) => handleDriverDataChange(driverName, 'expense1099', e.target.value)}
                                                    className="w-28 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    min="0"
                                                    step="1"
                                                />
                                            </td>
                                            <td className="py-3 px-4">
                                                <input
                                                    type="text"
                                                    value={data.comments}
                                                    onChange={(e) => handleDriverDataChange(driverName, 'comments', e.target.value)}
                                                    className="w-40 px-2 py-1 bg-white/10 border border-white/20 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="Notes..."
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Results */}
                {payResults.length > 0 && (
                    <div className="space-y-8">
                        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                                <FileText className="w-6 h-6 mr-3 text-blue-400" />
                                Pay Breakdown (Current Week)
                            </h2>

                            <div className="overflow-x-auto mb-6">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-white/20">
                                            <th className="text-left py-3 px-4 font-semibold text-gray-300">Driver</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-300">Daily Pay</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-300">Extra Hour Pay</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-300">Total Pay</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-300">1099 Expense</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-300">Total + 1099</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-300">Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payResults.sort((a, b) => a.name.localeCompare(b.name)).map(result => (
                                            <tr key={result.name} className="border-b border-white/10 hover:bg-white/5 transition-colors duration-200">
                                                <td className="py-3 px-4 text-white">{result.name}</td>
                                                <td className="py-3 px-4 text-white">${result.dailyPay.toFixed(2)}</td>
                                                <td className="py-3 px-4 text-white">${result.hourlyPay.toFixed(2)}</td>
                                                <td className="py-3 px-4 text-white">${result.regularPay.toFixed(2)}</td>
                                                <td className="py-3 px-4 text-white">${result.expense1099.toFixed(2)}</td>
                                                <td className="py-3 px-4 font-semibold text-green-400">${result.totalPay.toFixed(2)}</td>
                                                <td className="py-3 px-4 text-gray-400 text-sm">{result.comments}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="mt-8 text-center text-white bg-blue-700/30 rounded-xl p-4 border border-blue-600/50 shadow-lg">
                                <h2 className="text-3xl font-bold flex items-center justify-center">
                                    <TrendingUp className="w-8 h-8 mr-3 text-blue-400" />
                                    Total Payroll: ${totalPay.toFixed(2)}
                                </h2>
                            </div>
                        </div>
                    </div>
                )}

                {payResults.length === 0 && (
                    <div className="mt-8 text-center text-gray-400 bg-white/5 p-4 rounded-lg border border-white/10">
                        <p className="text-lg">Enter driver work details and click "Calculate & Save Current Pay" to see results.</p>
                        <p className="text-sm mt-2">Results will clear automatically when driver data is changed.</p>
                    </div>
                )}

                {/* Lookback Section */}
                <div className="mt-8 bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                    <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                        <Calendar className="w-6 h-6 mr-3 text-yellow-400" />
                        View Past Payrolls
                    </h2>
                    <div className="flex flex-wrap items-center gap-4">
                        <label htmlFor="archive-select" className="text-gray-300 text-lg mr-2">Select Week:</label>
                        <select
                            id="archive-select"
                            value={selectedArchiveId}
                            onChange={(e) => setSelectedArchiveId(e.target.value)}
                            className="px-4 py-2 bg-white border border-white/20 rounded-lg text-black focus:text-black focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 flex-grow"
                            disabled={isLoading || archivedWeeks.length === 0}
                        >
                            {archivedWeeks.length === 0 ? (
                                <option value="">No past payrolls available</option>
                            ) : (
                                <>
                                    <option value="">-- Select a Past Week --</option>
                                    {archivedWeeks.map(week => (
                                        <option key={week.id} value={week.id}>{week.displayDate}</option>
                                    ))}
                                </>
                            )}
                        </select>
                        <button
                            onClick={loadSelectedArchive}
                            disabled={isLoading || !selectedArchiveId}
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-500 hover:to-red-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Download className="w-5 h-5 mr-2" />
                            {isLoading ? 'Loading...' : 'Load Selected Payroll'}
                        </button>
                    </div>

                    {loadedArchiveDetails && (
                        <div className="mt-4 p-4 bg-white/5 rounded-lg text-gray-300 text-sm border border-white/10">
                            <h3 className="text-lg font-semibold text-white mb-2">Archived Payroll Summary:</h3>
                            {loadedArchiveDetails.payDate && <p><strong>Pay Date:</strong> {new Date(loadedArchiveDetails.payDate).toLocaleDateString()}</p>}
                            {loadedArchiveDetails.periodStartDate && loadedArchiveDetails.periodEndDate && (
                                <p><strong>Period:</strong> {loadedArchiveDetails.periodStartDate} to {loadedArchiveDetails.periodEndDate}</p>
                            )}
                            <p><strong>Archived On:</strong> {loadedArchiveDetails.archiveTimestamp ? new Date(loadedArchiveDetails.archiveTimestamp).toLocaleString() : 'N/A'}</p>
                            <p><strong>Total Drivers:</strong> {loadedArchiveDetails.numberOfDrivers}</p>
                            <p><strong>Total Payroll Amount:</strong> ${loadedArchiveDetails.totalPay ? loadedArchiveDetails.totalPay.toFixed(2) : '0.00'}</p>
                            <p><strong>Total Daily Pay:</strong> ${loadedArchiveDetails.totalDailyPay ? loadedArchiveDetails.totalDailyPay.toFixed(2) : '0.00'}</p>
                            <p><strong>Total Extra hour Pay:</strong> ${loadedArchiveDetails.totalHourlyPay ? loadedArchiveDetails.totalHourlyPay.toFixed(2) : '0.00'}</p>
                            <p><strong>Total 1099 Expense:</strong> ${loadedArchiveDetails.total1099Expense ? loadedArchiveDetails.total1099Expense.toFixed(2) : '0.00'}</p>
                            <p className="mt-2"><strong>Weekly Notes:</strong> {loadedArchiveDetails.weeklyNotes}</p>
                        </div>
                    )}
                    <p className="text-sm text-gray-400 mt-4 text-center">
                        **Important:** Loading a past payroll will replace the current data in the calculator for review. Remember to save if you make changes you want to keep for the *current* week before loading a past week.
                    </p>
                </div>

                {/* Archive Payroll Modal */}
                {showArchiveModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
                        <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-xl shadow-2xl border border-white/20 w-full max-w-md relative animate-fade-in-up">
                            <button
                                onClick={() => setShowArchiveModal(false)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200"
                                aria-label="Close"
                            >
                                <XCircle className="w-6 h-6" />
                            </button>
                            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                                <Save className="w-6 h-6 mr-3 text-green-400" />
                                Finalize & Archive Payroll
                            </h2>

                            <div className="mb-4">
                                <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="payDate">
                                    Payroll Pay Date: <span className="text-red-400">*</span>
                                </label>
                                <DatePicker
                                    selected={payDate}
                                    onChange={(date) => setPayDate(date)}
                                    className="w-full px-4 py-2 rounded-lg bg-white/10 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                    dateFormat="MM/dd/yyyy"
                                    placeholderText="Select pay date"
                                />
                                <p className="text-gray-400 text-xs mt-1">This date will be the primary identifier for this archived payroll record.</p>
                            </div>

                            <div className="mb-4">
                                <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="startDate">
                                    Payroll Period Start Date: <span className="text-red-400">*</span>
                                </label>
                                <DatePicker
                                    selected={periodStartDate}
                                    onChange={(date) => setPeriodStartDate(date)}
                                    selectsStart
                                    startDate={periodStartDate}
                                    endDate={periodEndDate}
                                    className="w-full px-4 py-2 rounded-lg bg-white/10 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                    dateFormat="MM/dd/yyyy"
                                    placeholderText="Select start date"
                                />
                            </div>

                            <div className="mb-6">
                                <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="endDate">
                                    Payroll Period End Date: <span className="text-red-400">*</span>
                                </label>
                                <DatePicker
                                    selected={periodEndDate}
                                    onChange={(date) => setPeriodEndDate(date)}
                                    selectsEnd
                                    startDate={periodStartDate}
                                    endDate={periodEndDate}
                                    minDate={periodStartDate}
                                    className="w-full px-4 py-2 rounded-lg bg-white/10 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                    dateFormat="MM/dd/yyyy"
                                    placeholderText="Select end date"
                                />
                            </div>

                            <div className="mb-6">
                                <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="modalNotes">
                                    Weekly Notes (for archive):
                                </label>
                                <textarea
                                    id="modalNotes"
                                    value={tempWeeklyNotes}
                                    onChange={(e) => setTempWeeklyNotes(e.target.value)}
                                    rows="3"
                                    placeholder="Add any specific notes for this archived payroll week..."
                                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                ></textarea>
                            </div>

                            <button
                                onClick={finalizeAndArchivePayroll}
                                disabled={isCalculating || !payDate || !periodStartDate || !periodEndDate}
                                className="w-full inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-700 text-white rounded-lg hover:from-green-500 hover:to-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isCalculating ? (
                                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                ) : (
                                    <Save className="w-5 h-5 mr-2" />
                                )}
                                Confirm & Archive
                            </button>
                        </div>
                    </div>
                )}

            </div>
            
            {/* DatePicker Styles */}
            <style jsx global>{`
                .react-datepicker-popper {
                    z-index: 9999 !important;
                }

                .react-datepicker {
                    background-color: #1e293b !important;
                    border: 1px solid rgba(255, 255, 255, 0.2) !important;
                    border-radius: 0.75rem !important;
                }

                .react-datepicker__header {
                    background-color: #334155 !important;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
                    border-radius: 0.75rem 0.75rem 0 0 !important;
                    padding-top: 0.5rem !important;
                }

                .react-datepicker__current-month,
                .react-datepicker__day-name {
                    color: #ffffff !important;
                }

                .react-datepicker__day {
                    color: #cbd5e1 !important;
                }

                .react-datepicker__day:hover {
                    background-color: #3b82f6 !important;
                    color: #ffffff !important;
                    border-radius: 0.5rem !important;
                }

                .react-datepicker__day--selected,
                .react-datepicker__day--keyboard-selected {
                    background-color: #3b82f6 !important;
                    color: #ffffff !important;
                    border-radius: 0.5rem !important;
                }

                .react-datepicker__day--in-range,
                .react-datepicker__day--in-selecting-range {
                    background-color: rgba(59, 130, 246, 0.3) !important;
                    color: #ffffff !important;
                }

                .react-datepicker__day--disabled {
                    color: #475569 !important;
                    cursor: not-allowed !important;
                }

                .react-datepicker__navigation-icon::before {
                    border-color: #cbd5e1 !important;
                }

                .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
                    border-color: #ffffff !important;
                }

                .react-datepicker__close-icon::after {
                    background-color: #ef4444 !important;
                    color: #ffffff !important;
                }
            `}</style>
        </div>
    );
};

export default DriverPayCalculator;